import QRCode from 'qrcode'
import React, { useEffect, useState } from 'react'
import { triggerSentryError } from '../../../../shared/helpers'
import Spinner from '../Spinner'
import type { QRCodeProps } from './types'

export default function QRCodeGenerator({
  text,
  width = 256,
  bgColor = '#ffffff',
  fgColor = '#000000',
  errorCorrectionLevel = 'M',
  margin = 4,
  className = '',
  fallbackContent = null,
}: QRCodeProps): React.ReactElement | null {
  const [qrImageUrl, setQrImageUrl] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [hasError, setHasError] = useState<boolean>(false)

  const generateQRCode = async () => {
    if (!text) {
      return
    }

    try {
      setIsLoading(true)
      setHasError(false)
      const url = await QRCode.toDataURL(text, {
        width,
        margin,
        color: {
          dark: fgColor,
          light: bgColor,
        },
        errorCorrectionLevel,
      })
      setQrImageUrl(url)
    } catch (err: any) {
      setHasError(true)
      triggerSentryError('generateQRCode', 'QRCodeGenerator.tsx', {
        errorMessage: err.message ?? 'Undefined error message (default)',
        errorLocation: 'QRCodeGenerator - generateQRCode - Generating QR code',
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    generateQRCode()
  }, [text, width, bgColor, fgColor, errorCorrectionLevel, margin])

  if (hasError || !text) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return fallbackContent ? <>{fallbackContent}</> : null
  }

  if (isLoading) {
    return <Spinner className="qr-code-generator__spinner" />
  }

  return (
    <img
      src={qrImageUrl}
      alt="QR Code"
      className={className}
      style={{ maxWidth: '100%', height: 'auto' }}
      loading="lazy"
    />
  )
}
